import React, { useState, useRef } from 'react';

// ── Validation ranges per distance ──────────────────────────────
const DIST_META = [
  { label: '5K',      km: 5,    showHours: false, range: '12:00 – 50:00',       min: 12*60,    max: 50*60         },
  { label: '10K',     km: 10,   showHours: true,  range: '25:00 – 1:20:00',     min: 25*60,    max: 80*60         },
  { label: '21.1',    km: 21.1, showHours: true,  range: '55:00 – 3:30:00',     min: 55*60,    max: 3*3600+30*60  },
  { label: '42.2',    km: 42.2, showHours: true,  range: '2:00:00 – 7:00:00',   min: 2*3600,   max: 7*3600        },
];

function getMeta(km) {
  if (!km) return null;
  return DIST_META.find(d => Math.abs(d.km - km) < 0.5) || null;
}

function parse(v) {
  if (!v) return { h: 0, m: 0, s: 0 };
  const parts = v.trim().split(':').map(Number);
  if (parts.length === 3) return { h: parts[0]||0, m: parts[1]||0, s: parts[2]||0 };
  if (parts.length === 2) return { h: 0, m: parts[0]||0, s: parts[1]||0 };
  return { h: 0, m: 0, s: 0 };
}

function fmtTime(h, m, s) {
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
}

// ── Single scrollable+typeable number field ──────────────────────
function NumField({ label, value, min, max, onChange }) {
  const [editing, setEditing] = useState(false);
  const [raw, setRaw] = useState('');
  const inputRef = useRef(null);

  const clamp = v => Math.max(min, Math.min(max, v));

  const handleWheel = (e) => {
    e.preventDefault();
    onChange(clamp(value + (e.deltaY < 0 ? 1 : -1)));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp')   { e.preventDefault(); onChange(clamp(value + 1)); }
    if (e.key === 'ArrowDown') { e.preventDefault(); onChange(clamp(value - 1)); }
  };

  const handleFocus = () => {
    setEditing(true);
    setRaw(String(value).padStart(2, '0'));
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const handleChange = (e) => {
    const v = e.target.value.replace(/\D/g, '').slice(0, 2);
    setRaw(v);
    const n = parseInt(v);
    if (!isNaN(n)) onChange(clamp(n));
  };

  const handleBlur = () => {
    setEditing(false);
    const n = parseInt(raw);
    if (!isNaN(n)) onChange(clamp(n));
  };

  const display = editing ? raw : String(value).padStart(2, '0');

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div style={{ fontSize: 11, color: 'var(--g-muted)', letterSpacing: 1 }}>{label}</div>
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={display}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        onWheel={handleWheel}
        onKeyDown={handleKeyDown}
        style={{
          width: '100%', textAlign: 'center',
          fontSize: 24, fontWeight: 700,
          fontFamily: 'ui-monospace, monospace',
          color: 'var(--g-text)',
          background: 'var(--g-bg)',
          border: '1.5px solid var(--g-line)',
          borderRadius: 10,
          padding: '8px 4px',
          outline: 'none',
          cursor: 'ns-resize',
          boxSizing: 'border-box',
          touchAction: 'none',
        }}
      />
      <div style={{ fontSize: 9, color: 'var(--g-muted)' }}>גלול / הקלד</div>
    </div>
  );
}

// ── Main TimePicker ─────────────────────────────────────────────
function TimePicker({
  label,
  value,
  onChange,
  onClose,
  showHours = true,
  distanceKm = null,
  // optional: pass array [{label,km}] to show distance selector inside picker
  distances = null,
  onDistanceChange = null,
}) {
  const [selKm, setSelKm] = useState(distanceKm);
  const [time, setTime] = useState(() => parse(value));

  const activeKm   = selKm || distanceKm;
  const meta       = getMeta(activeKm);
  const useHours   = meta ? meta.showHours : showHours;
  const totalSec   = time.h * 3600 + time.m * 60 + time.s;

  const paceStr = activeKm && totalSec > 0 ? (() => {
    const ps = totalSec / activeKm;
    return `${Math.floor(ps/60)}:${String(Math.round(ps%60)).padStart(2,'0')} דק'/ק"מ`;
  })() : null;

  const validStatus = meta && totalSec > 0
    ? totalSec < meta.min ? 'fast' : totalSec > meta.max ? 'slow' : 'ok'
    : null;

  const accentColor = { ok: 'var(--g-accent)', fast: 'var(--g-sub)', slow: 'var(--g-sub)' }[validStatus] || 'var(--g-line)';

  const confirm = () => {
    if (totalSec === 0) { onChange(''); onClose(); return; }
    const { h, m, s } = time;
    const mm = String(m).padStart(2,'0'), ss = String(s).padStart(2,'0');
    onChange((useHours && h > 0) ? `${h}:${mm}:${ss}` : `${mm}:${ss}`);
    if (onDistanceChange && selKm) onDistanceChange(selKm);
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--g-card)',
          borderRadius: 18,
          padding: '14px 14px 18px',
          width: '100%', maxWidth: 280,
          border: '1px solid var(--g-line)',
          direction: 'rtl',
        }}
      >
        {/* Title */}
        {label && (
          <p style={{ color: 'var(--g-text)', fontWeight: 700, fontSize: 15, textAlign: 'center', marginBottom: 4 }}>
            {label}
          </p>
        )}
        {distances && (
          <p style={{ color: 'var(--g-muted)', fontSize: 11, textAlign: 'center', marginBottom: 12 }}>
            בחר מרחק ואז הזן זמן
          </p>
        )}

        {/* Distance selector */}
        {distances && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, direction: 'ltr' }}>
            {distances.map(d => {
              const active = Math.abs((selKm||0) - d.km) < 0.5;
              return (
                <button
                  key={d.km}
                  type="button"
                  onClick={() => { setSelKm(d.km); setTime({ h: 0, m: 0, s: 0 }); }}
                  style={{
                    flex: 1, padding: '7px 2px', borderRadius: 10, border: 'none',
                    background: active ? 'var(--g-accent)' : 'var(--g-pressed)',
                    color: active ? 'var(--g-on-accent)' : 'var(--g-muted)',
                    fontWeight: 700, fontSize: 12, cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Number fields */}
        <div style={{ display: 'flex', gap: 8, direction: 'ltr' }}>
          {useHours && (
            <NumField label="שע׳" value={time.h} min={0} max={9}  onChange={h => setTime(t => ({...t,h}))} />
          )}
          <NumField label="דק׳" value={time.m} min={0} max={59} onChange={m => setTime(t => ({...t,m}))} />
          <NumField label="שנ׳" value={time.s} min={0} max={59} onChange={s => setTime(t => ({...t,s}))} />
        </div>

        {/* Validation range hint */}
        {meta && (
          <p style={{ textAlign: 'center', fontSize: 10, color: 'var(--g-muted)', marginTop: 8, direction: 'ltr' }}>
            {meta.label && `${meta.label} — `}{meta.range}
          </p>
        )}

        {/* Live time + pace */}
        {totalSec > 0 && (
          <div style={{
            marginTop: 12, padding: '10px 14px', borderRadius: 12,
            background: 'var(--g-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            direction: 'ltr',
          }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: accentColor, fontFamily: 'monospace' }}>
              {fmtTime(time.h, time.m, time.s)}
            </div>
            {paceStr && (
              <div style={{ fontSize: 13, fontWeight: 700, color: accentColor, fontFamily: 'monospace' }}>
                {paceStr}
              </div>
            )}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button
            type="button"
            onClick={() => { onChange(''); onClose(); }}
            style={{
              flex: 1, padding: '11px', borderRadius: 11,
              background: 'transparent', color: 'var(--g-sub)',
              fontWeight: 600, fontSize: 13, border: 'none', cursor: 'pointer',
            }}
          >
            נקה
          </button>
          <button
            type="button"
            onClick={confirm}
            style={{
              flex: 2, padding: '11px', borderRadius: 11,
              background: 'var(--g-accent)', color: 'var(--g-on-accent)',
              fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer',
            }}
          >
            אשר
          </button>
        </div>
      </div>
    </div>
  );
}

export default TimePicker;
